/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        User Workspace Shell Layout Component (Task 10.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect, useMemo } from 'react';
import { Sidebar } from '../components/navigation/Sidebar';
import { MobileDrawer } from '../components/navigation/MobileDrawer';
import { DashboardHeader } from '../components/header/DashboardHeader';
import { CreateWorkspaceModal } from '../components/workspace/CreateWorkspaceModal';
import { CommandPalette } from '../components/command/CommandPalette';
import { TopupModal } from '../components/wallet/TopupModal';
import { PaymentCheckoutModal } from '../components/billing/PaymentCheckoutModal';
import { InviteMemberModal } from '../components/team/InviteMemberModal';
import { useTheme } from '../context/ThemeProvider';
import { useAuthStore } from '../stores/useAuthStore';
import { useI18nStore } from '../stores/useI18nStore';
import { resolvePlatformDomains } from '@sofia/shared-types';
import { NAVIGATION_SECTIONS } from '../navigation/navigation-items';

export interface DashboardLayoutProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  currentPath,
  onNavigate,
  children,
}) => {
  const { themeData } = useTheme();
  const { user, clearSession } = useAuthStore();
  const { t } = useI18nStore();
  const siteName = themeData?.branding?.siteName || 'Sophia Platform';

  const handleExitImpersonation = () => {
    clearSession();
    const domains = resolvePlatformDomains();
    window.location.href = domains.adminOrigin;
  };

  // Find active navigation item and section for dynamic browser tab title
  const activeNav = useMemo(() => {
    for (const section of NAVIGATION_SECTIONS) {
      const foundItem = section.items.find((item) => item.path === currentPath);
      if (foundItem) {
        return { section, item: foundItem };
      }
    }
    return null;
  }, [currentPath]);

  // Synchronize document title and favicon
  useEffect(() => {
    const pageTitle = activeNav?.item?.label ?? 'Workspace';
    document.title = `${pageTitle} | ${siteName}`;

    if (themeData?.branding?.faviconUrl) {
      let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0]?.appendChild(link);
      }
      link.href = themeData.branding.faviconUrl;
    }
  }, [activeNav, siteName, themeData]);

  return (
    <div className="min-h-screen bg-background-base flex flex-col font-sans text-foreground transition-colors duration-200">
      {/* 0. Impersonation Top Amber Banner (Task 11.4.3) */}
      {user?.isImpersonated && (
        <aside
          aria-label="Impersonation Warning Banner"
          className="bg-amber-500 text-amber-950 px-4 py-2 text-xs font-semibold flex items-center justify-between sticky top-0 z-50 shadow-md border-b border-amber-600/30 shrink-0"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm">⚠️</span>
            <span>
              {t('impersonation.bannerNotice', { email: user.email })}
            </span>
          </div>
          <button
            type="button"
            onClick={handleExitImpersonation}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-950 text-amber-100 hover:bg-black font-semibold text-xs transition-colors ml-4 shadow-sm"
          >
            <span>{t('impersonation.exit')}</span>
          </button>
        </aside>
      )}

      <div className="flex-1 flex flex-col md:flex-row min-w-0">
        {/* 1. Desktop Collapsible Sidebar */}
        <Sidebar currentPath={currentPath} onNavigate={onNavigate} />

        {/* 2. Mobile Drawer Sheet */}
        <MobileDrawer currentPath={currentPath} onNavigate={onNavigate} />

        {/* 3. Main Application Column */}
        <div className="flex-1 flex flex-col min-w-0 min-h-screen">
          {/* Top Sticky Glassmorphism Header */}
          <DashboardHeader currentPath={currentPath} onNavigate={onNavigate} />

          {/* Scrollable Main Content Viewport */}
          <main className="flex-1 p-4 sm:p-6 md:p-8 overflow-y-auto max-w-7xl w-full mx-auto space-y-6 animate-fade-in">
            {children}
          </main>
        </div>
      </div>

      {/* 4. Global Modals & Command Palette */}
      <CreateWorkspaceModal />
      <TopupModal />
      <PaymentCheckoutModal />
      <InviteMemberModal />
      <CommandPalette onNavigate={onNavigate} />
    </div>
  );
};

